import Modal from '../../../components/Modal'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button
} from '@mui/material'
import MapIcon from '@mui/icons-material/Map'
import { AddressData } from '../repository/getAddresses'
import useAccount from '../hooks/useAccount'

export type AddressVerificationProps = {
  open: boolean
  onClose: () => void
  verifiedAddress: AddressData | null
  providedAddress: AddressData | null
  addressStatus: 'invalid' | 'suggested' | undefined
  addressError: string[]
}

const styles = {
  boxTitle: {
    display: 'flex',
    gap: '8px',
    padding: '16px 24px',
    alignItems: 'center',
    borderBottom: '1px solid #e0e0e0'
  },
  boldTitle: {
    fontWeight: 'bold',
    fontSize: '14px',
    marginTop: '16px',
    marginBottom: '8px'
  },
  content: {
    padding: '24px'
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    padding: '24px',
    borderTop: '1px solid #e0e0e0',
    marginTop: '16px'
  },
  contactUs: {
    textAlign: 'center',
    padding: '16px',
    borderTop: '1px solid #e0e0e0'
  },
  verifiedButton: {
    backgroundColor: '#00B3C7',
    color: 'white',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: '#009bb0'
    }
  },
  enteredButton: {
    color: 'inherit',
    padding: '8px 16px',
    border: '1px solid',
    borderColor: 'divider'
  }
}

const buildOneLineAddress = (address: AddressData) => {
  const parts = [
    address.address_1,
    address.address_2,
    address.city,
    address.state,
    address.zip,
    address.country
  ].filter((part) => part && part.trim() !== '')

  return parts.join(', ')
}

export const AddressVerification = ({
  open,
  onClose,
  verifiedAddress,
  providedAddress,
  addressStatus,
  addressError
}: AddressVerificationProps) => {
  const { updatePartnerAddress } = useAccount()

  const handleVerifiedAddress = () => {
    if (addressStatus === 'suggested' && verifiedAddress) {
      updatePartnerAddress(verifiedAddress, 'verified')
    }
    onClose()
  }

  const handleEnteredAddress = () => {
    const status = addressStatus === 'suggested' ? 'deliverable' : 'unverified'
    if (providedAddress) updatePartnerAddress(providedAddress, status)
    onClose()
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          maxWidth: 600,
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: 1
        }}
      >
        <Box sx={styles.boxTitle}>
          <MapIcon />
          <Typography variant="h6" data-testid="address-verification-modal">
            Address Verification
          </Typography>
        </Box>

        <Box sx={styles.content}>
          {addressStatus === 'invalid' && (
            <Typography sx={{ mb: 2 }}>
              We could not verify your address. Please double check your address
              and update it
            </Typography>
          )}
          {addressStatus === 'suggested' && (
            <Typography sx={{ mb: 2 }}>
              We could not verify your address. Please double check your address
              or select a verified address below.
            </Typography>
          )}

          {addressError.length > 0 ? (
            <Box>
              <Typography variant="h6">Errors:</Typography>
              <List>
                {addressError.map((error) => (
                  <ListItem key={error}>
                    <ListItemText
                      primary={error}
                      sx={{ color: 'error.main' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" sx={styles.boldTitle}>
                Suggested verified address:
              </Typography>
              <Typography>
                {verifiedAddress && buildOneLineAddress(verifiedAddress)}
              </Typography>
            </Box>
          )}

          <Typography variant="h6" sx={styles.boldTitle}>
            You entered:
          </Typography>
          <Typography>
            {providedAddress && buildOneLineAddress(providedAddress)}
          </Typography>
        </Box>

        <Box sx={styles.buttons}>
          <Button
            fullWidth
            variant="contained"
            sx={styles.verifiedButton}
            onClick={handleVerifiedAddress}
          >
            {addressStatus === 'suggested'
              ? 'Use Verified Address'
              : 'Edit your address'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            sx={styles.enteredButton}
            onClick={handleEnteredAddress}
          >
            Use Entered Address
          </Button>
        </Box>

        <Box sx={styles.contactUs}>
          <Typography variant="body2" color="textSecondary">
            Issues verifying your address?{' '}
            <Typography
              component="a"
              href="#"
              color="primary"
              sx={{ textDecoration: 'none' }}
            >
              Contact Us
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Modal>
  )
}
